import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AawtaLogo } from '../components/AawtaLogo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Loader2, Handshake, KanbanSquare, BarChart3, GraduationCap, CalendarDays, FolderLock, Mail, Phone,
} from 'lucide-react';

const FEATURES = [
  { icon: Handshake, title: 'Partnership CRM', desc: 'Manage partner relationships, sponsorships, contacts, and engagement activities.' },
  { icon: KanbanSquare, title: 'CRM & Pipeline Management', desc: 'Track leads, opportunities, partnership stages, and business development from prospect to partnership.' },
  { icon: BarChart3, title: 'Executive Dashboards & Analytics', desc: 'Monitor organizational performance with real-time dashboards, KPIs, reports, and partnership insights.' },
  { icon: GraduationCap, title: 'Workforce Development', desc: 'Coordinate workforce initiatives, apprenticeship programs, strategic partnerships, and community engagement.' },
  { icon: CalendarDays, title: 'Calendar & Meetings', desc: 'Schedule meetings, manage follow-ups, assign tasks, and stay connected with your team.' },
  { icon: FolderLock, title: 'Document Management', desc: 'Securely upload, organize, and access contracts, partnership agreements, proposals, and organizational resources.' },
];

const GOOGLE_ERRORS = {
  not_registered: 'This Google account is not registered in the Partnership Hub. Contact an administrator for access.',
  deactivated: 'Your account has been deactivated. Contact leadership.',
  token_exchange_failed: 'Google sign-in failed. Please try again.',
  no_email: 'Google did not return an email address. Please try again.',
  access_denied: 'Google sign-in was cancelled.',
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const err = searchParams.get('google_error');
    if (err) {
      if (err === 'not_registered') setAccessDenied(true);
      else toast.error(GOOGLE_ERRORS[err] || 'Google sign-in failed. Please try again.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await login(email, password, remember);
      toast.success('Welcome back!');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    try {
      const r = await api.get('/auth/google/signin-url');
      window.location.href = r.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Google sign-in is unavailable right now.');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8FAFC]">
      {/* Left — marketing panel */}
      <div className="lg:w-[55%] bg-navy text-white p-8 lg:p-14 flex flex-col relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-magenta/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-magenta/10 blur-3xl" />

        <div className="relative">
          <AawtaLogo size={48} dark />
        </div>

        <div className="relative z-10 flex-1 mt-10">
          <div className="inline-block bg-magenta/20 text-magenta border border-magenta/30 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wider uppercase mb-6">
            AAWTA Partnership Hub
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl font-bold leading-tight mb-5" data-testid="login-headline">
            Empowering Partnerships.<br />
            <span className="text-magenta">Driving Workforce Impact.</span>
          </h1>
          <p className="text-white/70 max-w-xl leading-relaxed mb-4">
            The AAWTA Partnership Hub is the centralized platform for managing partnerships, workforce development
            initiatives, sponsorships, events, and strategic collaboration—all in one secure, connected workspace.
          </p>
          <p className="text-white/50 max-w-xl text-sm leading-relaxed mb-10">
            Empowering leadership, directors, partners, sponsors, and team members to strengthen relationships,
            streamline operations, manage opportunities, and make informed decisions through one integrated platform
            designed to support organizational growth and workforce impact.
          </p>

          <div className="text-xs font-semibold tracking-wider uppercase text-magenta mb-4">
            Everything you need to manage partnerships in one place
          </div>
          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl" data-testid="login-features-grid">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-3 bg-white/5 border border-white/10 rounded-xl p-4 hover:border-magenta/40 transition-colors">
                <div className="shrink-0 w-9 h-9 rounded-lg bg-magenta/15 border border-magenta/30 flex items-center justify-center">
                  <Icon className="w-4.5 h-4.5 text-magenta" size={18} />
                </div>
                <div>
                  <div className="text-sm font-semibold mb-1">{title}</div>
                  <div className="text-xs text-white/55 leading-relaxed">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative mt-12 pt-6 border-t border-white/10 text-xs text-white/40 space-y-1" data-testid="login-footer">
          <div>© 2026 African American Women Trucking Association (AAWTA)</div>
          <div>Partnership Hub | Version 1.0 Beta</div>
          <div className="text-magenta/70">Connecting Partnerships. Creating Opportunities. Driving Impact.</div>
        </div>
      </div>

      {/* Right — login panel */}
      <div className="lg:w-[45%] flex items-center justify-center p-8 lg:p-14">
        <div className="w-full max-w-md">
          <h2 className="font-heading text-3xl font-bold text-navy mb-2">Welcome Back</h2>
          <p className="text-slate-500 text-sm leading-relaxed mb-8">
            Sign in to access your AAWTA Partnership Hub dashboard and manage partnerships, projects, and
            organizational activities from one secure platform.
          </p>

          {accessDenied && (
            <div className="mb-6 bg-rose-50 border border-rose-200 rounded-xl p-4" data-testid="access-denied-alert">
              <div className="font-semibold text-rose-700 text-sm mb-1">Access Denied</div>
              <p className="text-sm text-rose-600 leading-relaxed">
                Your Google account has not been authorized to access the AAWTA Partnership Hub.
                Please contact your system administrator for assistance.
              </p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password"
                className="mt-1.5"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600">
                <Checkbox
                  checked={remember}
                  onCheckedChange={(v) => setRemember(!!v)}
                  data-testid="login-remember-me"
                />
                Remember Me
              </label>
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-sm text-magenta font-medium hover:underline"
                data-testid="login-forgot-password"
              >
                Forgot Password?
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full bg-magenta hover:bg-magenta/90 text-white h-11 text-sm font-semibold"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Sign In
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs text-slate-400 uppercase tracking-wider">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={handleGoogle}
            disabled={googleLoading}
            className="w-full border border-slate-200 rounded-lg h-11 text-sm font-semibold text-slate-700 hover:border-magenta hover:text-magenta transition flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="google-login-button"
          >
            {googleLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
                <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18Z"/>
                <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17Z"/>
                <path fill="#FBBC05" d="M4.5 10.48a4.8 4.8 0 0 1 0-3.04V5.37H1.83a8 8 0 0 0 0 7.18l2.67-2.07Z"/>
                <path fill="#EA4335" d="M8.98 4.72c1.16 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.37L4.5 7.44a4.77 4.77 0 0 1 4.48-2.72Z"/>
              </svg>
            )}
            Continue with Your AAWTA Google Account
          </button>

          <p className="text-xs text-slate-400 text-center mt-4">
            Secure access for authorized AAWTA team members and approved partners.
          </p>

          <div className="mt-8 pt-6 border-t border-slate-100 text-center text-sm text-slate-500">
            <div className="mb-2">Need assistance accessing your account?</div>
            <div className="flex items-center justify-center gap-4 text-slate-600">
              <a href="mailto:contact@aawta.org" className="flex items-center gap-1.5 hover:text-magenta" data-testid="login-contact-email">
                <Mail size={14} /> contact@aawta.org
              </a>
              <a href="tel:7707566768" className="flex items-center gap-1.5 hover:text-magenta" data-testid="login-contact-phone">
                <Phone size={14} /> 770.756.6768
              </a>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-navy">Forgot your password?</DialogTitle>
            <DialogDescription className="pt-2 leading-relaxed">
              Password resets are handled by AAWTA administrators. Please contact your leadership team or reach out to{' '}
              <a href="mailto:contact@aawta.org" className="text-magenta font-medium hover:underline">contact@aawta.org</a>{' '}
              or call <span className="text-slate-700 font-medium">770.756.6768</span> and an administrator will issue you a temporary password.
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
